import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useToast } from '../hooks/use-toast';
import EducationalSlides from '../components/onboarding/EducationalSlides';
import PersonalityAssessment from '../components/onboarding/PersonalityAssessment';
import ArchetypeResults from '../components/onboarding/ArchetypeResults';
import FeatureTour from '../components/onboarding/FeatureTour';
import PWAInstallGuide from '../components/onboarding/PWAInstallGuide';

const OnboardingFlow = () => {
  const { user, token } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [currentPhase, setCurrentPhase] = useState('educational'); // educational, assessment, results, feature_tour, pwa_install
  const [assessmentAnswers, setAssessmentAnswers] = useState({});
  const [blueprint, setBlueprint] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Check if user already completed onboarding
  useEffect(() => {
    checkOnboardingStatus();
  }, []);

  const checkOnboardingStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/onboarding/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.onboarding_completed) {
          // User already completed onboarding, redirect to dashboard
          navigate('/app');
        }
      }
    } catch (error) {
      console.error('Error checking onboarding status:', error);
    }
  };

  const handleEducationalComplete = () => {
    setCurrentPhase('assessment');
  };

  const handleAssessmentComplete = async (answers) => {
    setAssessmentAnswers(answers);
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/onboarding/complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(answers),
      });

      if (response.ok) {
        const data = await response.json();
        setBlueprint(data.blueprint);
        setCurrentPhase('results');
        
        toast({
          title: "Assessment Complete! 🎉",
          description: "Your personalized blueprint is ready",
          duration: 5000,
        });
      } else {
        throw new Error('Failed to submit assessment');
      }
    } catch (error) {
      console.error('Error submitting assessment:', error);
      toast({
        title: "Error",
        description: "Failed to submit assessment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResultsComplete = () => {
    setCurrentPhase('feature_tour');
  };

  const handleTourComplete = () => {
    // Move to PWA installation guide
    setCurrentPhase('pwa_install');
  };

  const handlePWAComplete = () => {
    // Navigate to dashboard
    toast({
      title: "Welcome to Constant Love! 💖",
      description: "Your journey begins now. Let's grow together!",
      duration: 6000,
    });
    navigate('/app');
  };

  const handlePWASkip = () => {
    // Skip PWA and go directly to dashboard
    toast({
      title: "Welcome to Constant Love! 💖",
      description: "Your journey begins now. Let's grow together!",
      duration: 6000,
    });
    navigate('/app');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-rose-100">
      {currentPhase === 'educational' && (
        <EducationalSlides onComplete={handleEducationalComplete} />
      )}

      {currentPhase === 'assessment' && (
        <PersonalityAssessment 
          onComplete={handleAssessmentComplete}
          isSubmitting={isSubmitting}
        />
      )}

      {currentPhase === 'results' && blueprint && (
        <ArchetypeResults 
          blueprint={blueprint}
          userName={user?.name}
          onContinue={handleResultsComplete}
        />
      )}

      {currentPhase === 'feature_tour' && (
        <FeatureTour onComplete={handleTourComplete} />
      )}

      {currentPhase === 'pwa_install' && (
        <PWAInstallGuide 
          onComplete={handlePWAComplete}
          onSkip={handlePWASkip}
        />
      )}
    </div>
  );
};

export default OnboardingFlow;